import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { authClient } from "@/lib/auth-client";

interface User {
	id: string;
	email: string;
	name: string;
	image?: string | null;
	emailVerified: boolean;
	createdAt: Date;
	updatedAt: Date;
	siteRole?: string;
	isActive?: boolean;
	locale?: string;
}

interface AuthContextType {
	user: User | null;
	session: { id: string; userId: string; expiresAt: Date } | null;
	isLoading: boolean;
	isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
	user: null,
	session: null,
	isLoading: true,
	isAuthenticated: false,
});

function useAuthContext() {
	return useContext(AuthContext);
}

// Public routes that don't require authentication
const PUBLIC_ROUTES = ["/login", "/signup", "/invite"];

function isPublicRoute(pathname: string): boolean {
	return PUBLIC_ROUTES.some(
		(route) => pathname === route || pathname.startsWith(`${route}/`),
	);
}

interface AuthProviderProps {
	children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
	const location = useLocation();
	const pathname = location.pathname;
	const { data: session, isPending } = authClient.useSession();
	const { i18n } = useTranslation();
	const userId = session?.user.id;
	const savedLocale = session?.user.locale;
	const language = i18n.resolvedLanguage;
	// The server writes chat messages and emails in the profile's language: keep it the interface's.
	useEffect(() => {
		if (!userId || !language || savedLocale === language) return;
		void authClient.updateUser({ locale: language }).catch(() => {});
	}, [userId, savedLocale, language]);

	const isAuthenticated = !!session?.user;
	const isPublic = isPublicRoute(pathname);

	// Handle redirects synchronously in render (no useEffect)
	// Redirect unauthenticated users from protected routes
	if (!isPending && !isAuthenticated && !isPublic) {
		return (
			<Navigate
				to={`/login?callbackUrl=${encodeURIComponent(pathname)}`}
				replace
			/>
		);
	}

	// Redirect authenticated users away from login/signup, to the first page
	// they can open ("/" works that out).
	if (
		!isPending &&
		isAuthenticated &&
		(pathname === "/login" || pathname === "/signup")
	) {
		return <Navigate to="/" replace />;
	}

	return (
		<AuthContext.Provider
			value={{
				user: (session?.user as User | null) ?? null,
				session: session?.session ?? null,
				isLoading: isPending,
				isAuthenticated,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
}

/**
 * Component wrapper that only renders children if user is authenticated
 * Shows loading state while checking authentication
 */
interface ProtectedRouteProps {
	children: ReactNode;
	fallback?: ReactNode;
}

export function ProtectedRoute({ children, fallback }: ProtectedRouteProps) {
	const { isLoading, isAuthenticated } = useAuthContext();

	if (isLoading) {
		return (
			fallback || (
				<div className="flex min-h-screen items-center justify-center">
					<div className="flex flex-col items-center gap-4">
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-text-primary border-t-transparent" />
						<p className="text-text-secondary">Loading...</p>
					</div>
				</div>
			)
		);
	}

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	return <>{children}</>;
}

/**
 * Component wrapper that requires admin access
 */
interface AdminRouteProps {
	children: ReactNode;
	fallback?: ReactNode;
}

export function AdminRoute({ children, fallback }: AdminRouteProps) {
	const { isLoading, isAuthenticated, user } = useAuthContext();

	const isAdmin =
		user?.siteRole === "admin" || user?.siteRole === "super_admin";

	if (isLoading) {
		return (
			fallback || (
				<div className="flex min-h-screen items-center justify-center">
					<div className="flex flex-col items-center gap-4">
						<div className="h-8 w-8 animate-spin rounded-full border-4 border-text-primary border-t-transparent" />
						<p className="text-text-secondary">Loading...</p>
					</div>
				</div>
			)
		);
	}

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	// "/" sends the user to their main page.
	if (!isAdmin) {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
}
